import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lightning, CheckCircle, Warning } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Landing page after the $15 Crunchy message top-up Stripe checkout.
 * Polls /billing/topup/status until the payment is confirmed + credited.
 */
export default function TopupSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | done | timeout | error
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/topup/status/${sessionId}`);
        if (data.credited) {
          setResult(data);
          setState('done');
          return;
        }
      } catch (_) { /* keep polling */ }
      if (attempts >= 12) { setState('timeout'); return; }
      setTimeout(poll, 3000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="topup-success-page">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm max-w-md w-full p-8 text-center">
        {state === 'polling' && (
          <>
            <Lightning size={40} weight="fill" className="text-[#F39C12] mx-auto mb-4 animate-pulse" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Confirming your top-up…</h1>
            <p className="text-sm text-slate-500">Checking with Stripe — this usually takes a few seconds.</p>
          </>
        )}
        {state === 'done' && (
          <>
            <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2" data-testid="topup-confirmed">
              +{result?.credits_added || 200} Crunchy messages added!
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              They never expire and kick in automatically after your free monthly {' '}
              allowance runs out. Bonus balance: <strong>{result?.bonus_balance}</strong> messages.
            </p>
            <Link
              to="/dashboard"
              className="inline-block bg-[#0B5394] hover:bg-[#094180] text-white font-bold text-sm rounded px-5 py-2.5"
              data-testid="topup-back-btn"
            >
              Back to my CRM →
            </Link>
          </>
        )}
        {(state === 'timeout' || state === 'error') && (
          <>
            <Warning size={40} weight="fill" className="text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Still processing</h1>
            <p className="text-sm text-slate-500 mb-6">
              Your payment may still be confirming. Your messages will be credited
              automatically once Stripe confirms — no action needed. If they don&apos;t
              appear within a few minutes, email chip@migrationcrunch.com.
            </p>
            <Link to="/dashboard" className="inline-block bg-[#0B5394] hover:bg-[#094180] text-white font-bold text-sm rounded px-5 py-2.5">
              Back to my CRM →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
